import { ColumnLayout, Header } from '@amzn/awsui-components-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../store/user.context';
import { i18nKeys } from '../../utils/i18n.utils';
import {
  aboutAwsJamVideo,
  challengeAuthoringGuideUrl,
  challengeWritingStyleGuideUrl,
  eventOwnerGuideVideo,
  jamIntroPptUrl,
  participantsJoiningEventVideo,
  skillBuilderSupportUrl,
  supportContactsUrl,
  userGuideUrl,
} from '../../utils/jam-urls';
import DocumentationCard from './DocumentationCard';

export interface DocumentationLink {
  href: string;
  label: string;
  description: string;
  canView: boolean;
  disabled?: boolean;
}

export interface DocumentationGroup {
  title: string;
  documentLinks: DocumentationLink[];
  videoLinks?: DocumentationLink[];
}

const Documentation: React.FC = () => {
  const { isLoggedIn, user } = useUser();
  const { t } = useTranslation();

  const DocumentationGroupLinks: DocumentationGroup[] = [
    {
      title: t(i18nKeys.documentation.cards.general.title),
      documentLinks: [
        {
          href: jamIntroPptUrl,
          label: t(i18nKeys.documentation.cards.general.documents.overviewPresentation.label),
          description: t(i18nKeys.documentation.cards.general.documents.overviewPresentation.description),
          canView: isLoggedIn,
        },
      ],
      videoLinks: [
        {
          href: aboutAwsJamVideo,
          label: t(i18nKeys.documentation.cards.general.documents.aboutAwsJam.label),
          description: t(i18nKeys.documentation.cards.general.documents.aboutAwsJam.description),
          canView: isLoggedIn,
        },
      ],
    },
    {
      title: t(i18nKeys.documentation.cards.userGuides.title),
      documentLinks: [
        {
          href: userGuideUrl,
          label: t(i18nKeys.documentation.cards.userGuides.documents.userGuide.label),
          description: t(i18nKeys.documentation.cards.userGuides.documents.userGuide.description),
          canView: isLoggedIn,
        },
        {
          href: '#',
          label: t(i18nKeys.documentation.cards.userGuides.documents.genAiGuide.label),
          description: t(i18nKeys.documentation.cards.userGuides.documents.genAiGuide.description),
          canView: isLoggedIn,
          disabled: true,
        },
      ],
      videoLinks: [
        {
          href: participantsJoiningEventVideo,
          label: t(i18nKeys.documentation.cards.userGuides.documents.participantsJoiningEvent.label),
          description: t(i18nKeys.documentation.cards.userGuides.documents.participantsJoiningEvent.description),
          canView: isLoggedIn,
        },
        {
          href: eventOwnerGuideVideo,
          label: t(i18nKeys.documentation.cards.userGuides.documents.eventOwnerGuide.label),
          description: t(i18nKeys.documentation.cards.userGuides.documents.eventOwnerGuide.description),
          canView: isLoggedIn,
        },
      ],
    },
    {
      title: t(i18nKeys.documentation.cards.challenges.title),
      documentLinks: [
        {
          href: challengeAuthoringGuideUrl,
          label: t(i18nKeys.documentation.cards.challenges.documents.challengeAuthoringGuide.label),
          description: t(i18nKeys.documentation.cards.challenges.documents.challengeAuthoringGuide.description),
          canView: (isLoggedIn && (user?.isChallengeAdmin || user?.isChallengeBuilder)) || false,
        },
        {
          href: challengeWritingStyleGuideUrl,
          label: t(i18nKeys.documentation.cards.challenges.documents.challengeWritingStyleGuide.label),
          description: t(i18nKeys.documentation.cards.challenges.documents.challengeAuthoringGuide.description),
          canView: (isLoggedIn && (user?.isChallengeAdmin || user?.isChallengeBuilder)) || false,
        },
      ],
    },
    {
      title: t(i18nKeys.documentation.cards.support.title),
      documentLinks: [
        {
          href: supportContactsUrl,
          label: t(i18nKeys.documentation.cards.support.documents.supportContacts.label),
          description: t(i18nKeys.documentation.cards.support.documents.supportContacts.description),
          canView: isLoggedIn && !user?.isTCUser,
        },
        {
          href: skillBuilderSupportUrl,
          label: t(i18nKeys.documentation.cards.support.documents.supportContacts.label),
          description: t(i18nKeys.documentation.cards.support.documents.supportContacts.description),
          canView: (isLoggedIn && user?.isTCUser) || false,
        },
      ],
    },
  ];

  return (
    <React.Fragment>
      <Header variant="h1" description={t(i18nKeys.documentation.description)}>
        {t(i18nKeys.documentation.title)}
      </Header>
      <ColumnLayout columns={4}>
        {DocumentationGroupLinks.map((documentGroup: DocumentationGroup) => {
          return (
            <DocumentationCard
              key={`document-card-${documentGroup.title}`}
              title={documentGroup.title}
              documentationLinks={documentGroup.documentLinks}
              videoLinks={documentGroup.videoLinks || undefined}
            />
          );
        })}
      </ColumnLayout>
    </React.Fragment>
  );
};
export default Documentation;
